import React, { useState, useEffect } from "react";
import { fetchData } from "../../../../utils/functions";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import NoData, { hasData } from "../../../common/NoData";
import DashboardBarChart from "../../../common/DashboardBarChart";

const EMPTY_EMPLOYMENT = {
  overall: { worked: 0, notWorked: 0, total: 0 },
  male: { worked: 0, notWorked: 0, total: 0 },
  female: { worked: 0, notWorked: 0, total: 0 },
  age10to15: { worked: 0, notWorked: 0, total: 0 },
  age16to17: { worked: 0, notWorked: 0, total: 0 },
  age18plus: { worked: 0, notWorked: 0, total: 0 },
};

const EmploymentTab = ({ filters }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [data, setData] = useState({
    employmentStats: EMPTY_EMPLOYMENT,
    occupationStats: [],
  });
  const [loading, setLoading] = useState(true);

  const formatCount = (num) =>
    num?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") || "0";

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [employmentRes, occupationRes] = await Promise.all([
          fetchData(
            "employment-statistics",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/employment-statistics",
            dispatch
          ),
          fetchData(
            "occupation-statistics",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/occupation-statistics",
            dispatch
          ),
        ]);

        setData({
          employmentStats: employmentRes.data || EMPTY_EMPLOYMENT,
          occupationStats: occupationRes.data || [],
        });
      } catch (err) {
        console.error("Error loading Employment tab:", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [filters, navigate, dispatch]);

  // Employment Chart
  const employmentCategories = [
    "Age 18+",
    "Age 16-17",
    "Age 10-15",
    "Female",
    "Male",
    "Overall",
  ];

  const employmentSeries = [
    {
      name: "Worked",
      data: [
        data.employmentStats.age18plus.worked,
        data.employmentStats.age16to17.worked,
        data.employmentStats.age10to15.worked,
        data.employmentStats.female.worked,
        data.employmentStats.male.worked,
        data.employmentStats.overall.worked,
      ],
    },
    {
      name: "Not Worked",
      data: [
        data.employmentStats.age18plus.notWorked,
        data.employmentStats.age16to17.notWorked,
        data.employmentStats.age10to15.notWorked,
        data.employmentStats.female.notWorked,
        data.employmentStats.male.notWorked,
        data.employmentStats.overall.notWorked,
      ],
    },
  ];

  const totalOccupations = data.occupationStats.reduce(
    (sum, item) => sum + (item.count || 0),
    0
  );

  const has = {
    employment: hasData(employmentSeries.map((s) => s.data)),
    occupation: hasData(totalOccupations),
  };

  const anyData = Object.values(has).some(Boolean);
  const showRow = () => loading || anyData;
  const panel = (hasPanelData, body) =>
    !loading && !hasPanelData ? <NoData /> : body;

  return (
    <div className="space-y-4 pb-12">
      {!loading && !Object.values(has).some(Boolean) && (
        <NoData className="py-16" />
      )}
      {/* Row 1: Employment Statistics and Nature of Work */}
      {showRow(has.employment, has.occupation) && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Employment Statistics */}
        <div className="bg-white flex flex-col h-full">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Employment Statistics
          </h3>
          <p className="text-center text-sm text-gray-600 mb-4">
            Members Age 10+ Who Have Worked for Pay
          </p>
          {panel(has.employment,
          <div className="flex-1">
            <DashboardBarChart
              horizontal
              categories={employmentCategories}
              series={employmentSeries}
              unit="persons"
            />
          </div>)}
        </div>

        {/* Nature of Work (Occupation) */}
        <div className="bg-white flex flex-col h-full">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Nature of Work (Occupation)
          </h3>
          <p className="text-center text-sm text-gray-600 mb-4">
            Total Occupations Recorded: {formatCount(totalOccupations)}
          </p>
          {panel(has.occupation,
          <DashboardBarChart
            horizontal
            items={data.occupationStats.map((item) => ({
              name: item.occupation,
              count: item.count || 0,
            }))}
            unit="persons"
          />)}
        </div>
      </div>
      )}
    </div>
  );
};

export default EmploymentTab;
